/* eslint-disable camelcase, no-undef */
class BrochureForm extends HTMLElement {
  constructor () {
    super()
    this.webhookUrl = this.getAttribute('data-webhook-url')
    this.postcodePlaceholder = this.getAttribute('data-postcode-placeholder')
    this.postcodeButton = this.getAttribute('data-postcode-button')
    this.addressSelectPlaceholder = this.getAttribute('data-address-select-placeholder')
    this.form = this.querySelector('form')
    this.submitButton = this.querySelector('button[type="submit"]')
    this.successMessage = this.querySelector('.success-message')
    this.addressDropdown = this.querySelector('.address-dropdown')
    this.addressOptions = this.querySelector('.address-options')
    this.changeAddressBtn = this.querySelector('.change-address')
    this.postcodeInput = this.querySelector('input[name="postcode"]')
    this.phoneInput = this.querySelector('input[name="phone"]')
    this.autocomplete = null
    this.selectedAddressData = null
    this.apiKey = 'ak_m84cpiz6zphDmqCw1lQtyMnVr2bkB'
    this.recaptchaSiteKey = document.querySelector('script[src*="recaptcha/api.js"]')?.src.split('render=')[1]
    this.recaptchaSecretKey = 'YOUR_RECAPTCHA_SECRET_KEY' // Replace with actual secret key

    this.init()
  }

  init () {
    window.Tentbox.loadScript(
      'https://cdn.jsdelivr.net/npm/@ideal-postcodes/postcode-lookup-bundled@2',
      () => {
        this.setupPostcodeLookup()
        this.setupFormValidation()
        this.setupPhoneValidation()
        this.setupFormSubmission()
      },
    )
  }

  setupFormValidation () {
    // Just handle the submit button state based on address selection
    this.submitButton.disabled = !this.selectedAddressData

    if (this.submitButton.disabled) {
      this.submitButton.classList.add('opacity-60', 'cursor-not-allowed')
      this.submitButton.classList.remove('cursor-pointer')
    } else {
      this.submitButton.classList.remove('opacity-60', 'cursor-not-allowed')
      this.submitButton.classList.add('cursor-pointer')
    }
  }

  setupPhoneValidation () {
    this.phoneInput.addEventListener('input', (e) => {
      const value = e.target.value.replace(/\D/g, '')

      if (value.length > 0) {
        let formattedValue = value

        if (value.startsWith('44')) {
          formattedValue = value.substring(2)
        }

        e.target.value = formattedValue
      } else {
        e.target.value = value
      }
    })
  }

  setupPostcodeLookup () {
    this.postcodeInput.classList.add('uppercase')

    // Create hidden fields for address data
    const addressFields = document.createElement('div')
    addressFields.classList.add('hidden')
    addressFields.innerHTML = `
      <input type="hidden" id="line_1">
      <input type="hidden" id="line_2">
      <input type="hidden" id="line_3">
      <input type="hidden" id="post_town">
      <input type="hidden" id="postcode">
    `
    this.form.appendChild(addressFields)

    // Create context div for Ideal Postcodes dropdown
    const context = document.createElement('div')
    context.id = 'postcode-lookup'
    this.postcodeInput.parentNode.insertBefore(context, this.postcodeInput)

    // Initialize Ideal Postcodes lookup
    IdealPostcodes.PostcodeLookup.setup({
      apiKey: this.apiKey,
      context: '#postcode-lookup',
      outputFields: {
        line_1: '#line_1',
        line_2: '#line_2',
        line_3: '#line_3',
        post_town: '#post_town',
        postcode: '#postcode',
      },
      placeholder: this.postcodePlaceholder || 'Search your postcode',
      buttonLabel: this.postcodeButton || 'Find address',
      onAddressSelected: (address) => {
        this.handleAddressSelection(address)
      },
      onLoaded: () => {
        this.stylePostcodeLookup()
      },
    })

    // Hide the original postcode input
    this.postcodeInput.classList.add('hidden')

    // Remove the change address button functionality since we don't need it anymore
    const changeAddressBtn = this.querySelector('.change-address')
    if (changeAddressBtn) {
      changeAddressBtn.remove()
    }
  }

  stylePostcodeLookup () {
    const container = document.querySelector('#postcode-lookup')
    if (!container) return

    // Style the container with responsive grid
    container.style.display = 'grid'
    container.style.gap = '1rem'
    // Default to stacked on mobile, side by side on desktop
    container.style.gridTemplateColumns = '1fr'
    // Add media query for desktop
    const mediaQuery = window.matchMedia('(min-width: 768px)')
    const updateLayout = (e) => {
      container.style.gridTemplateColumns = e.matches ? '1fr auto' : '1fr'
    }
    mediaQuery.addListener(updateLayout)
    updateLayout(mediaQuery)

    // Style the input and add postcode-specific attributes
    const input = container.querySelector('.idpc-input')
    if (input) {
      input.className = 'h-11.5 w-full rounded border border-steel px-3.5 py-2.5 md:px-4.5 md:py-3 placeholder-battleship focus-visible:outline-hidden text-base text-black focus:ring-0 focus:border-black hover:border-black'
      input.setAttribute('autocomplete', 'postal-code')
      input.setAttribute('inputmode', 'text')
      input.setAttribute('type', 'text')
      input.setAttribute('pattern', '[A-Za-z0-9 ]*')
      input.setAttribute('spellcheck', 'false')
    }

    // Style the button
    const button = container.querySelector('.idpc-button')
    if (button) {
      button.className = 'button button-orange h-11.5'
      button.setAttribute('tabindex', '0')
    }

    // Create a specific observer for the address dropdown container to detect when it changes
    this.setupSelectObserver(container)

    // Style the select when it appears - this is our main observer for structure changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          const selectContainer = container.querySelector('.idpc-select-container')
          if (selectContainer) {
            selectContainer.style.gridColumn = '1 / -1'

            // Move margin-top to the container instead of the select
            selectContainer.style.marginTop = '0.5rem'

            // Setup a new observer specifically for this select container if not done already
            if (!selectContainer.dataset.observerSetup) {
              this.setupSelectObserver(container)
              selectContainer.dataset.observerSetup = 'true'
            }
          }

          const select = container.querySelector('.idpc-select')
          if (select) {
            // Remove mt-2 from select since we moved it to the container
            select.className = 'h-11.5 w-full rounded border border-steel px-3.5 py-2.5 md:px-4.5 md:py-3 placeholder-battleship focus-visible:outline-hidden text-base text-black focus:ring-0 focus:border-black hover:border-black'
            select.setAttribute('tabindex', '0')

            // Always update the first option regardless of new search
            this.updateFirstOption(select)

            // Only trigger animation if we detect this is a newly added select
            if (!select.dataset.processed) {
              select.dataset.processed = 'true'
              this.createBlinkingOutline(select)
            }
          }
        }
      })
    })

    observer.observe(container, { childList: true, subtree: true })

    // Also add a click listener to the button to track when new searches start
    const searchButton = container.querySelector('.idpc-button')
    if (searchButton) {
      searchButton.addEventListener('click', () => {
        this.needsAnimation = true

        // Reset any processed flags on existing selects
        const existingSelect = container.querySelector('.idpc-select')
        if (existingSelect) {
          existingSelect.removeAttribute('data-processed')
        }
      })
    }
  }

  setupSelectObserver (container) {
    // Create a specific observer to watch for changes to the select container or its children
    const selectObserver = new MutationObserver((mutations) => {
      // Check each mutation
      mutations.forEach(mutation => {
        // If nodes were added
        if (mutation.addedNodes.length) {
          // Check if a select element was added
          mutation.addedNodes.forEach(node => {
            if (node.nodeName === 'SELECT' || (node.querySelector && node.querySelector('select'))) {
              const select = node.nodeName === 'SELECT' ? node : node.querySelector('select')
              if (select) {
                // Always update the first option
                this.updateFirstOption(select)

                // Remove the processed flag to ensure animation runs
                select.removeAttribute('data-processed')

                // Trigger animation
                this.createBlinkingOutline(select)
              }
            }
          })
        }

        // If attributes changed on a select element
        if (mutation.type === 'attributes' && mutation.target.nodeName === 'SELECT') {
          // Always update the first option
          this.updateFirstOption(mutation.target)

          // Check if this is a new set of options by tracking the first actual address option
          if (mutation.target.options.length > 1) {
            const secondOptionValue = mutation.target.options[1].value
            if (secondOptionValue !== this.lastSecondOptionValue) {
              this.lastSecondOptionValue = secondOptionValue
              // Trigger animation for new options
              this.createBlinkingOutline(mutation.target)
            }
          }
        }

        // If the options inside a select changed
        if (mutation.target.nodeName === 'SELECT' && mutation.type === 'childList') {
          // Always update the first option
          this.updateFirstOption(mutation.target)

          // Trigger animation for new options
          this.createBlinkingOutline(mutation.target)
        }
      })
    })

    // Start observing the container for select changes
    selectObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value', 'options'],
    })

    return selectObserver
  }

  updateFirstOption (select) {
    if (select && select.options && select.options.length > 0) {
      const firstOption = select.options[0]
      firstOption.text = this.addressSelectPlaceholder || 'Select your address'
      firstOption.disabled = true
      firstOption.selected = true
    }
  }

  createBlinkingOutline (select) {
    if (!select) return

    // Remove any existing outline
    const existingOutline = select.parentNode.querySelector('.select-outline-debug')
    if (existingOutline) {
      existingOutline.remove()
    }

    // Wait 200ms before creating the outline
    setTimeout(() => {
      // Check if the select is still in the DOM
      if (!document.body.contains(select)) return

      // Create outline div with debug class
      const outlineDiv = document.createElement('div')
      outlineDiv.className = 'select-outline-debug border-2 border-orange rounded-md'

      // Add a span with sr-only text to avoid :empty selector hiding it
      const srSpan = document.createElement('span')
      srSpan.className = 'sr-only'
      srSpan.textContent = 'Address dropdown outline'
      outlineDiv.appendChild(srSpan)

      // Set positioning styles directly
      Object.assign(outlineDiv.style, {
        position: 'absolute',
        top: '-4px',
        left: '-4px',
        right: '-4px',
        bottom: '-4px',
        pointerEvents: 'none',
        zIndex: '10',
        boxSizing: 'border-box',
        opacity: '0', // Start invisible
        transition: 'opacity 150ms ease', // Add smooth transition for fading
      })

      // Ensure parent has position relative
      select.parentNode.style.position = 'relative'

      // Append to select parent
      select.parentNode.appendChild(outlineDiv)

      // Fade in and out sequence using opacity
      setTimeout(() => {
        outlineDiv.style.opacity = '1' // Fade in
      }, 0)

      setTimeout(() => {
        if (document.body.contains(outlineDiv)) {
          outlineDiv.style.opacity = '0' // Fade out
        }
      }, 500)

      setTimeout(() => {
        if (document.body.contains(outlineDiv)) {
          outlineDiv.style.opacity = '1' // Fade in again
        }
      }, 1000)

      setTimeout(() => {
        if (document.body.contains(outlineDiv)) {
          outlineDiv.style.opacity = '0' // Fade out finally
        }
      }, 1500)
    }, 200)
  }

  handleAddressSelection (address) {
    this.selectedAddressData = {
      line_1: document.querySelector('#line_1').value,
      line_2: document.querySelector('#line_2').value,
      line_3: document.querySelector('#line_3').value,
      post_town: document.querySelector('#post_town').value,
      postcode: document.querySelector('#postcode').value,
    }

    // Update postcode input with selected postcode
    this.postcodeInput.value = this.selectedAddressData.postcode

    // Enable submit button after address selection
    this.setupFormValidation()
  }

  setupFormSubmission () {
    this.form.addEventListener('submit', async (e) => {
      e.preventDefault()

      if (this.loading) return

      const phoneNumber = this.phoneInput.value.replace(/\D/g, '')

      if (!phoneNumber.match(/^(44|0)?(07|7)[1-9][0-9]{8}$/)) {
        alert('Please enter a valid UK mobile number')
        return
      }

      this.loading = true
      try {
        let recaptchaToken = null
        if (this.recaptchaSiteKey) {
          try {
            recaptchaToken = await this.executeRecaptcha()
          } catch (error) {
            console.error('reCAPTCHA error:', error)
            alert('Error verifying request. Please try again.')
            return
          }
        }

        const formData = new FormData(this.form)

        const sanitizedPhone = phoneNumber.startsWith('07')
          ? phoneNumber.substring(1)
          : phoneNumber.startsWith('0')
            ? phoneNumber.substring(1)
            : phoneNumber.startsWith('44')
              ? phoneNumber.substring(2)
              : phoneNumber

        const data = {
          first_name: formData.get('first_name'),
          last_name: formData.get('last_name'),
          email: formData.get('email'),
          phone: `+44${sanitizedPhone}`,
          postcode: formData.get('postcode'),
          address: this.selectedAddressData,
          recaptcha_token: recaptchaToken,
        }

        const response = await fetch(this.webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ data }),
        })

        if (response.ok) {
          this.querySelector('.brochure-form__container').classList.add('hidden')
          this.successMessage.classList.remove('hidden')
          this.successMessage.classList.add('border', 'border-steel', 'rounded-lg', 'p-4', 'max-w-lg', 'mx-auto')

          // Show the tick with display:block, which will trigger the animation
          const tickContainer = this.querySelector('.brochure-success-tick')
          if (tickContainer) {
            tickContainer.style.display = 'block'
          }

          // Scroll the success message into view
          this.successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' })

          this.loading = false
        } else {
          this.loading = false
          throw new Error(`Submission failed: ${response.status} ${response.statusText}`)
        }
      } catch (error) {
        console.error('Error:', error)
        alert('There was an error submitting the form. Please try again.')
        this.loading = false
      }
    })
  }

  async executeRecaptcha () {
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha) {
        reject(new Error('reCAPTCHA not loaded'))
        return
      }

      window.grecaptcha.enterprise.ready(() => {
        window.grecaptcha.enterprise.execute(this.recaptchaSiteKey, { action: 'brochure_form_submit' })
          .then(async (token) => {
            try {
              // Create the assessment request
              const assessmentRequest = {
                event: {
                  token,
                  expectedAction: 'brochure_form_submit',
                  siteKey: this.recaptchaSiteKey,
                },
              }

              // Send assessment request to your backend
              const response = await fetch('https://recaptchaenterprise.googleapis.com/v1/projects/brochure-form-1741706571477/assessments?key=API_KEY', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(assessmentRequest),
              })

              if (!response.ok) {
                throw new Error('Failed to verify reCAPTCHA token')
              }

              const result = await response.json()

              // Check if the token is valid and the risk score is acceptable
              if (result.tokenProperties?.valid && result.riskAnalysis?.score > 0.5) {
                resolve(token)
              } else {
                reject(new Error('reCAPTCHA verification failed'))
              }
            } catch (error) {
              reject(error)
            }
          })
          .catch(reject)
      })
    })
  }

  set loading (value) {
    this.setAttribute('loading', value)
  }

  get loading () {
    return this.getAttribute('loading') === 'true'
  }
}

if (!customElements.get('brochure-form')) {
  customElements.define('brochure-form', BrochureForm)
}
/* eslint-enable camelcase, no-undef */
